import { useMoney } from '../context/LocationContext';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  BookOpen,
  Copy,
  Loader2,
  Plug,
  RefreshCw,
  ShieldCheck,
  Trash2,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Zap,
  Receipt,
  Wand2,
  Wallet,
  Save,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';

export default function QuickBooks() {
  const { token } = useAuth();
  const { money } = useMoney();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const [config, setConfig] = useState({ configured: false });
  const [status, setStatus] = useState({ connected: false });
  const [salesCfg, setSalesCfg] = useState({ sales_config: null, realtime_enabled: true });
  const [summary, setSummary] = useState({
    pending: 0,
    posted_today: 0,
    posted_total: 0,
    recent: [],
    realtime_enabled: true,
    last_realtime_poll_at: null,
  });
  const [settingUp, setSettingUp] = useState(false);
  const [syncingNow, setSyncingNow] = useState(false);
  const [togglingRealtime, setTogglingRealtime] = useState(false);
  const [depositAccounts, setDepositAccounts] = useState([]);
  const [paymentMap, setPaymentMap] = useState({ mappings: [], payment_methods: [] });
  const [savingMap, setSavingMap] = useState(false);

  const [form, setForm] = useState({
    client_id: '',
    client_secret: '',
    environment: 'sandbox',
  });

  const refresh = async () => {
    try {
      const [c, s] = await Promise.all([
        api.getQboAppCredentials(token),
        api.getQboStatus(token),
      ]);
      setConfig(c.data);
      setStatus(s.data);
      if (c.data?.configured) {
        setForm((f) => ({
          ...f,
          client_id: c.data.client_id || '',
          environment: c.data.environment || 'sandbox',
        }));
      }
      if (s.data?.connected) {
        const [cfg, sum, mp] = await Promise.all([
          api.getQboSalesConfig(token),
          api.getQboSalesSummary(token),
          api.getQboPaymentMappings(token),
        ]);
        setSalesCfg(cfg.data);
        setSummary(sum.data);
        setPaymentMap(mp.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [token]);

  // Handle Intuit callback return
  useEffect(() => {
    const s = searchParams.get('status');
    if (!s) return;
    if (s === 'connected') {
      toast.success('QuickBooks connected successfully');
    } else if (s === 'error') {
      const reason = searchParams.get('reason') || 'unknown';
      toast.error(`QuickBooks connection failed: ${reason}`);
    }
    setSearchParams({}, { replace: true });
    refresh();
  }, [searchParams]);

  const handleSave = async () => {
    if (!form.client_id.trim() || !form.client_secret.trim()) {
      toast.error('Client ID and Client Secret are both required');
      return;
    }
    setSaving(true);
    try {
      await api.saveQboAppCredentials(token, {
        client_id: form.client_id.trim(),
        client_secret: form.client_secret.trim(),
        environment: form.environment,
      });
      toast.success('Credentials saved. You can now connect to QuickBooks.');
      setForm((f) => ({ ...f, client_secret: '' }));
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const r = await api.getQboAuthorizeUrl(token);
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start OAuth flow');
      setConnecting(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await api.testQboConnection(token);
      setStatus((s) => ({ ...s, company_name: r.data.company_name || s.company_name }));
      toast.success(`Connected to ${r.data.company_name || 'QuickBooks'}`);
      await refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Connection failed';
      const isAuthIssue = /reconnect|token|401|403|reauthorization/i.test(detail);
      toast.error(detail);
      if (isAuthIssue) {
        // Refresh status so the reauth banner appears + auto-scroll to it
        await refresh();
      }
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect QuickBooks? Tokens will be revoked and removed.')) return;
    setDisconnecting(true);
    try {
      await api.disconnectQbo(token);
      toast.success('QuickBooks disconnected');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const copyRedirect = async () => {
    if (!config?.redirect_uri) return;
    try {
      await navigator.clipboard.writeText(config.redirect_uri);
      toast.success('Redirect URI copied to clipboard');
    } catch {
      toast.error('Copy failed — please copy manually');
    }
  };

  // ---- Sales posting handlers ------------------------------------------------
  const refreshSales = async () => {
    if (!status.connected) return;
    try {
      const [cfg, sum] = await Promise.all([
        api.getQboSalesConfig(token),
        api.getQboSalesSummary(token),
      ]);
      setSalesCfg(cfg.data);
      setSummary(sum.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!status.connected) return;
    const t = setInterval(refreshSales, 10000);
    return () => clearInterval(t);
  }, [status.connected, token]);

  const handleAutoMap = async () => {
    setSettingUp(true);
    try {
      const r = await api.qboSalesSetup(token);
      const c = r.data.sales_config;
      toast.success(`Auto-mapped: item "${c.item_name}" · customer "${c.customer_name}" · deposit "${c.deposit_account_name}"`);
      await refreshSales();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-map failed');
    } finally {
      setSettingUp(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncingNow(true);
    try {
      const r = await api.qboSalesSyncNow(token);
      const { posted, failed, errors } = r.data;
      if (posted && !failed) toast.success(`Posted ${posted} sales to QuickBooks`);
      else if (posted && failed) toast.success(`Posted ${posted}, ${failed} failed`);
      else if (failed) toast.error(`0 posted, ${failed} failed. ${errors?.[0] || ''}`);
      else toast.info('Nothing to post — all sales are up to date');
      await refreshSales();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncingNow(false);
    }
  };

  const [postingTest, setPostingTest] = useState(false);
  const handlePostTestSale = async () => {
    setPostingTest(true);
    try {
      const r = await api.qboPostTestSale(token);
      toast.success(r.data.message);
      await refreshSales();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test sale failed');
    } finally {
      setPostingTest(false);
    }
  };

  const handleToggleRealtime = async (v) => {
    setTogglingRealtime(true);
    // optimistic
    setSummary((s) => ({ ...s, realtime_enabled: v }));
    try {
      await api.qboSalesToggleRealtime(token, v);
      toast.success(v ? 'Real-time posting enabled' : 'Real-time posting paused');
      await refreshSales();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
      setSummary((s) => ({ ...s, realtime_enabled: !v }));
    } finally {
      setTogglingRealtime(false);
    }
  };

  // ---- Payment method → deposit account mapping ----------------------------
  const loadDepositAccounts = async () => {
    try {
      const r = await api.getQboDepositAccounts(token);
      setDepositAccounts(r.data.accounts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load QuickBooks deposit accounts');
    }
  };

  useEffect(() => {
    if (status.connected && salesCfg.sales_config) {
      loadDepositAccounts();
    }
  }, [status.connected, salesCfg.sales_config]);

  const setMappingFor = (method, accountId) => {
    setPaymentMap((prev) => {
      const others = (prev.mappings || []).filter((m) => m.payment_method !== method);
      const acct = depositAccounts.find((a) => a.id === accountId);
      const row = accountId
        ? {
            payment_method: method,
            deposit_account_id: accountId,
            deposit_account_name: acct?.name || null,
          }
        : null;
      return {
        ...prev,
        mappings: row ? [...others, row] : others,
      };
    });
  };

  const handleSaveMappings = async () => {
    // Build one row per known payment method (empty ones will be cleared server-side).
    const rows = (paymentMap.payment_methods || []).map((pm) => {
      const existing = (paymentMap.mappings || []).find((m) => m.payment_method === pm);
      return {
        payment_method: pm,
        deposit_account_id: existing?.deposit_account_id || null,
        deposit_account_name: existing?.deposit_account_name || null,
      };
    });
    setSavingMap(true);
    try {
      const r = await api.saveQboPaymentMappings(token, rows);
      toast.success(`Saved ${r.data.saved} mapping${r.data.saved === 1 ? '' : 's'}`);
      const mp = await api.getQboPaymentMappings(token);
      setPaymentMap(mp.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save mappings');
    } finally {
      setSavingMap(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const isConnected = status.connected;
  const step1Done = !!config.configured;
  const step2Done = isConnected;
  const step3Done = isConnected && !!status.company_name;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="quickbooks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
              QuickBooks Online
            </h1>
            <p className="text-slate-400 mt-1">
              Connect your UnTill POS to QuickBooks — securely and in three steps.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isConnected ? (
            <Badge
              className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 gap-1.5"
              data-testid="qbo-status-badge"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Connected
              {status.environment && (
                <span className="text-emerald-300/70 ml-1">· {status.environment}</span>
              )}
            </Badge>
          ) : status.reauthorization_required ? (
            <Badge
              className="bg-amber-500/15 text-amber-300 border-amber-500/30 gap-1.5"
              data-testid="qbo-status-badge"
            >
              <AlertCircle className="w-3.5 h-3.5" /> Reconnection needed
            </Badge>
          ) : (
            <Badge
              className="bg-slate-500/15 text-slate-400 border-slate-500/30 gap-1.5"
              data-testid="qbo-status-badge"
            >
              <AlertCircle className="w-3.5 h-3.5" /> Not connected
            </Badge>
          )}
        </div>
      </div>

      {/* Reauthorisation prompt — refresh token expired or Intuit revoked */}
      {status.reauthorization_required && (
        <Card className="bg-amber-500/10 border-amber-500/40" data-testid="qbo-reauth-card">
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex items-start gap-3 flex-1">
              <AlertCircle className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-amber-100 font-medium">Your QuickBooks session has expired</div>
                <p className="text-amber-200/80 text-sm mt-0.5">
                  Intuit refresh tokens expire after 100 days. Click <b>Reconnect QuickBooks</b> below
                  to sign in again — your saved item, customer and account mappings will be preserved.
                </p>
              </div>
            </div>
            <Button
              onClick={handleConnect}
              disabled={connecting || !step1Done}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              data-testid="qbo-reconnect-btn"
            >
              {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plug className="w-4 h-4 mr-2" />}
              Reconnect QuickBooks
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Steps */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <StepBadge n={1} title="Save credentials" done={step1Done} active={!step1Done} />
        <StepBadge n={2} title="Authorize Intuit" done={step2Done} active={step1Done && !step2Done} />
        <StepBadge n={3} title="Verify company" done={step3Done} active={step2Done && !step3Done} />
      </div>

      {/* Step 1 — credentials */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="qbo-credentials-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            Step 1 · Your Intuit app credentials
          </CardTitle>
          <CardDescription className="text-slate-400">
            Paste the Client ID and Client Secret from your Intuit Developer app. These stay
            encrypted on our side and are never shown again.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Client ID (public)</Label>
              <Input
                placeholder="AB1XXXXXXXXXXXXXX"
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm"
                data-testid="qbo-client-id-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Client Secret (private)</Label>
              <Input
                type="password"
                placeholder={config.configured ? '•••••••• (already saved — paste to change)' : 'Paste your Client Secret'}
                value={form.client_secret}
                onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                className="bg-slate-800/50 border-slate-700 text-white font-mono text-sm"
                data-testid="qbo-client-secret-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Environment</Label>
              <Select
                value={form.environment}
                onValueChange={(v) => setForm({ ...form, environment: v })}
              >
                <SelectTrigger
                  className="bg-slate-800/50 border-slate-700 text-white"
                  data-testid="qbo-environment-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="sandbox">Sandbox (recommended for setup)</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {config.redirect_uri && (
              <div className="space-y-2">
                <Label className="text-slate-300">Redirect URI (register this in your Intuit app)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={config.redirect_uri}
                    readOnly
                    className="bg-slate-800/50 border-slate-700 text-emerald-300 font-mono text-xs"
                    data-testid="qbo-redirect-uri"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={copyRedirect}
                    className="border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800"
                    data-testid="qbo-copy-redirect-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="qbo-save-credentials-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              {config.configured ? 'Update credentials' : 'Save credentials'}
            </Button>
            <a
              href="https://developer.intuit.com/app/developer/dashboard"
              target="_blank"
              rel="noreferrer"
              className="text-sm text-slate-400 hover:text-emerald-400 inline-flex items-center gap-1 self-center ml-1"
              data-testid="qbo-intuit-dev-link"
            >
              Open Intuit Developer dashboard <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Step 2 — connect */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="qbo-connect-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <Plug className="w-5 h-5 text-emerald-400" />
            Step 2 · Authorize QuickBooks
          </CardTitle>
          <CardDescription className="text-slate-400">
            You&apos;ll be redirected to Intuit to approve access, then brought back here automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            onClick={handleConnect}
            disabled={!step1Done || connecting}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="qbo-connect-btn"
          >
            {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plug className="w-4 h-4 mr-2" />}
            {isConnected ? 'Reconnect QuickBooks' : 'Connect QuickBooks'}
          </Button>
          {!step1Done && (
            <span className="text-xs text-slate-500 self-center">
              Save your credentials first.
            </span>
          )}
        </CardContent>
      </Card>

      {/* Step 3 — verify + manage */}
      <Card className="bg-slate-900/50 border-slate-800" data-testid="qbo-verify-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-emerald-400" />
            Step 3 · Verify & manage
          </CardTitle>
          <CardDescription className="text-slate-400">
            Ping the QuickBooks CompanyInfo API to confirm the link is live.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isConnected && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <InfoTile label="Company" value={status.company_name || '—'} />
              <InfoTile label="Realm ID" value={status.realm_id || '—'} mono />
              <InfoTile label="Environment" value={status.environment} />
              <InfoTile
                label="Access expires"
                value={
                  status.access_expires_at
                    ? new Date(status.access_expires_at).toLocaleString()
                    : '—'
                }
              />
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleTest}
              disabled={!isConnected || testing}
              variant="outline"
              className="border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800"
              data-testid="qbo-test-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Test connection
            </Button>
            <Button
              onClick={handleDisconnect}
              disabled={!isConnected || disconnecting}
              variant="outline"
              className="border-rose-500/40 text-rose-300 hover:text-white hover:bg-rose-500/20"
              data-testid="qbo-disconnect-btn"
            >
              {disconnecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Disconnect
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Live sales posting */}
      {isConnected && (
        <Card
          className="bg-gradient-to-br from-emerald-500/[0.03] to-slate-900/50 border-emerald-500/20"
          data-testid="qbo-sales-card"
        >
          <CardHeader>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
                  <Zap className="w-5 h-5 text-emerald-400" />
                  Live sales posting
                </CardTitle>
                <CardDescription className="text-slate-400">
                  New UnTill receipts are posted to QuickBooks as Sales Receipts every 30 seconds.
                </CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-slate-300 text-sm">Real-time</Label>
                <Switch
                  checked={!!summary.realtime_enabled}
                  disabled={togglingRealtime || !salesCfg.sales_config}
                  onCheckedChange={handleToggleRealtime}
                  data-testid="qbo-realtime-switch"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Setup banner */}
            {!salesCfg.sales_config && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 flex items-start gap-3">
                <Wand2 className="w-5 h-5 text-amber-300 mt-0.5" />
                <div className="flex-1">
                  <div className="text-amber-100 font-medium">Set up account mapping first</div>
                  <div className="text-amber-200/70 text-sm mt-1">
                    We&apos;ll auto-create (or reuse) a &quot;UnTill POS Sales&quot; item, pick
                    Undeposited Funds as the deposit account, and use a Cash Customer.
                  </div>
                </div>
                <Button
                  onClick={handleAutoMap}
                  disabled={settingUp}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-medium"
                  data-testid="qbo-automap-btn"
                >
                  {settingUp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  Auto-map QuickBooks accounts
                </Button>
              </div>
            )}

            {/* Counters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile
                label="Pending"
                value={summary.pending}
                accent="amber"
                testId="qbo-stat-pending"
              />
              <StatTile
                label="Posted today"
                value={summary.posted_today}
                accent="emerald"
                testId="qbo-stat-posted-today"
              />
              <StatTile
                label="Posted total"
                value={summary.posted_total}
                accent="blue"
                testId="qbo-stat-posted-total"
              />
              <StatTile
                label="Last poll"
                value={
                  summary.last_realtime_poll_at
                    ? new Date(summary.last_realtime_poll_at).toLocaleTimeString()
                    : '—'
                }
                accent="slate"
                testId="qbo-stat-last-poll"
              />
            </div>

            {/* Mapping summary */}
            {salesCfg.sales_config && (
              <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3 text-xs text-slate-400 flex flex-wrap gap-x-6 gap-y-1">
                <span>Item: <span className="text-slate-200 font-mono">{salesCfg.sales_config.item_name}</span></span>
                <span>Customer: <span className="text-slate-200 font-mono">{salesCfg.sales_config.customer_name}</span></span>
                <span>Deposit: <span className="text-slate-200 font-mono">{salesCfg.sales_config.deposit_account_name || '—'}</span></span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAutoMap}
                  disabled={settingUp}
                  className="ml-auto h-6 px-2 text-slate-400 hover:text-white"
                  data-testid="qbo-remap-btn"
                >
                  {settingUp ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Re-map'}
                </Button>
              </div>
            )}

            {/* Sync now */}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleSyncNow}
                disabled={!salesCfg.sales_config || syncingNow}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="qbo-sync-now-btn"
              >
                {syncingNow ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                Post now
              </Button>
              <Button
                onClick={handlePostTestSale}
                disabled={!salesCfg.sales_config || postingTest}
                variant="outline"
                className="border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 hover:text-white"
                data-testid="qbo-test-sale-btn"
              >
                {postingTest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Receipt className="w-4 h-4 mr-2" />}
                Post test sale
              </Button>
              {summary.pending > 0 && (
                <span className="text-xs text-amber-300/80 self-center">
                  {summary.pending} sale{summary.pending === 1 ? '' : 's'} waiting to post
                </span>
              )}
            </div>

            {/* Recent posts */}
            {summary.recent?.length > 0 && (
              <div className="rounded-lg border border-slate-800 overflow-hidden" data-testid="qbo-recent-posts">
                <div className="px-3 py-2 bg-slate-900/60 text-xs uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <Receipt className="w-3.5 h-3.5" /> Recent posts to QuickBooks
                </div>
                <div className="divide-y divide-slate-800/70">
                  {summary.recent.map((r) => (
                    <div key={r.id} className="px-3 py-2 flex items-center gap-3 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      <span className="text-slate-200 font-mono text-xs">{r.receipt_number}</span>
                      <span className="text-slate-400 truncate">{r.payment_method}</span>
                      <span className="ml-auto text-emerald-300 font-mono">
                        {money(r.total, r.location_id)}
                      </span>
                      <span className="text-slate-500 font-mono text-xs">
                        QBO#{r.qbo_doc_number || r.qbo_receipt_id}
                      </span>
                      <span className="text-slate-500 text-xs w-20 text-right">
                        {new Date(r.qbo_posted_at).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payment method → deposit account mapping */}
      {isConnected && salesCfg.sales_config && (
        <Card className="bg-slate-900/50 border-slate-800" data-testid="qbo-mapping-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-400" />
              Payment method → QuickBooks deposit account
            </CardTitle>
            <CardDescription className="text-slate-400">
              Point each UnTill payment method at the QuickBooks account where those funds should land.
              Anything left blank uses the default deposit account
              (<span className="text-emerald-300 font-mono">{salesCfg.sales_config?.deposit_account_name || '—'}</span>).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {depositAccounts.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">
                Loading QuickBooks deposit accounts…
              </div>
            ) : (
              <div className="rounded-lg border border-slate-800 overflow-hidden">
                <div className="grid grid-cols-12 px-3 py-2 bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500">
                  <div className="col-span-5">UnTill payment method</div>
                  <div className="col-span-7">QuickBooks deposit account</div>
                </div>
                <div className="divide-y divide-slate-800/70 max-h-96 overflow-y-auto">
                  {(paymentMap.payment_methods || []).map((pm) => {
                    const current = (paymentMap.mappings || []).find(
                      (m) => m.payment_method === pm
                    );
                    return (
                      <div
                        key={pm}
                        className="grid grid-cols-12 items-center px-3 py-2 gap-2"
                        data-testid={`qbo-map-row-${pm}`}
                      >
                        <div className="col-span-5 text-sm text-white font-mono truncate">{pm}</div>
                        <div className="col-span-7">
                          <Select
                            value={current?.deposit_account_id || 'none'}
                            onValueChange={(v) => setMappingFor(pm, v === 'none' ? null : v)}
                          >
                            <SelectTrigger
                              className="bg-slate-800/50 border-slate-700 text-white text-sm h-9"
                              data-testid={`qbo-map-select-${pm}`}
                            >
                              <SelectValue placeholder="Use default deposit account" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-800 max-h-64">
                              <SelectItem value="none">
                                <span className="text-slate-400">Use default deposit account</span>
                              </SelectItem>
                              {depositAccounts.map((a) => (
                                <SelectItem key={a.id} value={a.id}>
                                  <span className="text-white">{a.name}</span>
                                  <span className="text-slate-500 text-xs ml-2">
                                    {a.sub_type || a.type}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button
                onClick={handleSaveMappings}
                disabled={savingMap || depositAccounts.length === 0}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="qbo-save-mappings-btn"
              >
                {savingMap ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save mappings
              </Button>
              <Button
                variant="outline"
                onClick={loadDepositAccounts}
                className="border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800"
                data-testid="qbo-refresh-accounts-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh accounts
              </Button>
              <span className="text-xs text-slate-500 ml-2">
                {(paymentMap.mappings || []).length} of {(paymentMap.payment_methods || []).length} mapped
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* How to get the credentials */}
      <Card className="bg-slate-900/30 border-slate-800/60">
        <CardHeader>
          <CardTitle className="text-base font-heading text-white">Where do I get my Client ID and Client Secret?</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-400 space-y-2 leading-relaxed">
          <p><span className="text-slate-200 font-medium">1.</span> Go to the <a className="text-emerald-400 hover:underline" href="https://developer.intuit.com/app/developer/dashboard" target="_blank" rel="noreferrer">Intuit Developer dashboard</a> and sign in with your QuickBooks account.</p>
          <p><span className="text-slate-200 font-medium">2.</span> Create a new app of type <span className="text-slate-200">&quot;QuickBooks Online and Payments&quot;</span>. Only the <span className="text-slate-200">com.intuit.quickbooks.accounting</span> scope is required.</p>
          <p><span className="text-slate-200 font-medium">3.</span> Under <span className="text-slate-200">Keys &amp; OAuth</span>, copy the Client ID and Client Secret for the environment you want (Sandbox or Production).</p>
          <p><span className="text-slate-200 font-medium">4.</span> Register this exact Redirect URI in your Intuit app:
            <span className="font-mono text-emerald-300 ml-1 break-all">{config.redirect_uri || '(save credentials first)'}</span>
          </p>
          <p><span className="text-slate-200 font-medium">5.</span> Paste the two keys above, pick the environment, save, then click <span className="text-slate-200">Connect QuickBooks</span>.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function StepBadge({ n, title, done, active }) {
  return (
    <div
      className={
        'rounded-lg border p-4 flex items-center gap-3 ' +
        (done
          ? 'bg-emerald-500/10 border-emerald-500/30'
          : active
          ? 'bg-slate-900/60 border-emerald-500/20'
          : 'bg-slate-900/40 border-slate-800')
      }
      data-testid={`qbo-step-${n}`}
    >
      <div
        className={
          'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ' +
          (done
            ? 'bg-emerald-500 text-white'
            : active
            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
            : 'bg-slate-800 text-slate-500')
        }
      >
        {done ? <CheckCircle2 className="w-4 h-4" /> : n}
      </div>
      <div>
        <div className={done ? 'text-emerald-300 font-medium' : 'text-white font-medium'}>
          {title}
        </div>
        <div className="text-xs text-slate-500">
          {done ? 'Complete' : active ? 'In progress' : 'Pending'}
        </div>
      </div>
    </div>
  );
}

function InfoTile({ label, value, mono }) {
  return (
    <div className="rounded-lg bg-slate-800/40 border border-slate-800 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={'text-sm text-white mt-1 truncate ' + (mono ? 'font-mono' : '')}>
        {value}
      </div>
    </div>
  );
}

function StatTile({ label, value, accent = 'slate', testId }) {
  const color =
    accent === 'emerald'
      ? 'text-emerald-300'
      : accent === 'amber'
      ? 'text-amber-300'
      : accent === 'blue'
      ? 'text-blue-300'
      : 'text-slate-200';
  return (
    <div
      className="rounded-lg bg-slate-900/60 border border-slate-800 p-3"
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={'text-2xl font-bold font-mono mt-1 ' + color}>{value}</div>
    </div>
  );
}
